/**
 * The ContactUs component is a collection of form fields required to capture a user's details in order to get requirements via email.
 *
 * @module views/Molecules/ContactUs
 * @memberof -Common
 */
import './ContactUs.scss';

import React, { useRef } from 'react';

import { Form } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import { FormikWithPersist } from '@ulta/core/components/FormikWithPersist/FormikWithPersist';
import InputArea from '@ulta/core/components/InputArea/InputArea';
import InputField from '@ulta/core/components/InputField/InputField';
import Text from '@ulta/core/components/Text/Text';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import { constants } from '@ulta/utils/constants/constants';
import { addYupCustomRegex, formatModuleParamsForDXL, isRequiredValidation } from '@ulta/utils/Validations/Validations';

import DependentField from '../DependentField/DependentField';
import * as utils from './ContactUs';

/**
 * Represents a ContactUs component
 *
 * @method
 * @param {ContactUsProps} props - React properties passed from composition
 * @returns ContactUs
 */
export const ContactUs = function( props ){
  const {
    title,
    subTitle,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    loyalty,
    order,
    submitAction,
    comments,
    invokeMutation,
    allTopics
  } = props;

  const formikRef = useRef();
  const [loader, showLoader] = useLoader( { loading: false, isPageLoader: true, delay: 50 } );

  const validationSchema =  utils.getValidationSchema( {
    firstName,
    lastName,
    emailAddress,
    comments
  } );

  if( !firstName && !lastName && !emailAddress && !comments ){
    return null;
  }

  return (
    <div className='ContactUs'>
      { loader }
      <div className='ContactUs__Heading'>
        <Text htmlTag='h1'
          textStyle='title-2'
          textAlign='center'
        >
          { title }
        </Text>
        <Text htmlTag='p'
          textStyle='title-6'
          textAlign='center'
        >
          { subTitle }
        </Text>
      </div>
      <FormikWithPersist
        innerRef={ formikRef }
        initialValues={ utils.initialValues }
        validationSchema={ validationSchema }
        validateOnBlur={ true }
        validateOnChange={ true }
        validateOnMount
        enableReinitialize={ true }
        onSubmit={ ( values ) => {
          utils.handleSubmit( { values, submitAction }, { invokeMutation, showLoader } );
        } }
      >
        { ( { values, errors, touched, isValid, dirty } ) => (
          <Form className='ContactUs__Form'>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn'>
                <InputField
                  { ...spreadProps( { ...firstName } ) }
                  autoComplete='given-name'
                />
              </div>
              <div className='ContactUs__FormColumn'>
                <InputField
                  { ...spreadProps( { ...lastName } ) }
                  autoComplete='family-name'
                />
              </div>
            </div>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn'>
                <InputField
                  { ...spreadProps( { ...emailAddress } ) }
                  autoComplete='email'
                />
              </div>
              <div className='ContactUs__FormColumn'>
                <InputField
                  { ...spreadProps( { ...phoneNumber } ) }
                  formatter={ constants.FORMATTER_PATTERNS.PHONE_NO_FORMATTER }
                  autoComplete='tel'
                />
              </div>
            </div>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn'>
                <InputField { ...spreadProps( { ...loyalty } ) }/>
              </div>
              <div className='ContactUs__FormColumn'>
                <InputField { ...spreadProps( { ...order } ) }/>
              </div>
            </div>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn'>
                <DependentField
                  field={ allTopics.field[0] }
                  dependentFields={ allTopics.dependentFields }
                  name='allTopics'
                />
              </div>
            </div>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn'>
                <InputArea
                  name='comments'
                  label={ comments.label }
                  maxLength={ comments.maxLength }
                  placeholder={ comments.placeholderLabel }
                  instructionalText={ `${ comments.helpText } ${
                    comments.maxLength - ( values.comments ? values.comments.length : 0 )
                  } characters remaining` }
                  disabled={ false }
                  ariaId='contactInfoArea'
                  message={ touched.comments && errors.comments }
                  messageType={ touched.comments && errors.comments ? 'error' : '' }
                />
              </div>
            </div>
            <div className='ContactUs__FormRow'>
              <div className='ContactUs__FormColumn ContactUs__FormColumn--submit'>
                <Button
                  type='submit'
                  withHover
                  label={ submitAction.label }
                  action={ submitAction }
                  ariaLabel={ submitAction.label }
                  fullWidth={ true }
                  { ...( !dirty || !isValid ? { disabled: true } : {} ) }
                />
              </div>
            </div>
          </Form>
        ) }
      </FormikWithPersist>
    </div>
  );
};

export const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  loyalty: '',
  order: '',
  comments: '',
  allTopics: '',
  allTopics_2: '',
  allTopics_3: ''
};

/**
 * @function handleSubmit
 * @description - handles Submit Button
 * @param {Object} methods - Passing methods as an argument for the mutation call
 * @param {String} data - Passing data as an argument
 */
export const handleSubmit = ( data, methods ) => {
  const { values, submitAction } = data || {};
  const { invokeMutation, showLoader } = methods || {};
  const { graphql, params, customHeaders, dataCaptureData } = submitAction || {};

  if( !graphql || !invokeMutation || !showLoader ){
    return;
  }

  const moduleParams = formatModuleParamsForDXL( { values } );

  showLoader();

  invokeMutation( {
    after: utils.scrollPageToTop,
    dataCaptureData,
    graphql,
    customHeaders,
    params,
    variables: {
      moduleParams,
      url: { path: global.location.pathname }
    }
  } );
};

/**
* A handler function for manually setting form fields
* @method getValidationSchema
* @param {Object} data - Contains input fields and their Amplience configurations
* @returns {Object} validationSchema - Returns Object containing Yup validations for each passed in field.
*/
export const getValidationSchema = ( data ) => {
  const {
    firstName,
    lastName,
    emailAddress,
    comments
  } = handleEmptyObjects( data );

  addYupCustomRegex( { schemaType: Yup.string, name: 'firstName', validations: firstName?.validations } );
  addYupCustomRegex( { schemaType: Yup.string, name: 'lastName', validations: lastName?.validations } );
  addYupCustomRegex( { schemaType: Yup.string, name: 'email', validations: emailAddress?.validations } );
  addYupCustomRegex( { schemaType: Yup.string, name: 'comments', validations: comments?.validations } );

  // define required check yup method
  Yup.addMethod( Yup.string, 'isRequiredField', isRequiredValidation );

  return Yup.object( {
    firstName: firstName?.validations?.[0] && Yup.string().isRequiredField( firstName ).firstName(),
    lastName: lastName?.validations?.[0] && Yup.string().isRequiredField( lastName ).lastName(),
    email: emailAddress?.validations?.[0] && Yup.string().isRequiredField( emailAddress ).email( emailAddress.validations[0].message ),
    comments: comments?.validations?.[0] && Yup.string()
      .isRequiredField( comments )
      .max( comments.maxLength )
      .comments( comments.validations[0].message ),
    allTopics: Yup.string().required( 'Please Select an option' ),
    allTopics_2: Yup.string().required( 'Please Select an option' ),
    allTopics_3: Yup.string().required( 'Please Select an option' )
  } );
};

/**
 * Scroll page to top when mutation api call completes
 */
export const scrollPageToTop = () => {
  scrollToElement( { el:document.body } );
};

/**
 * Property type definitions
 * @typedef ContactUsProps
 * @type {object}
 * @property {string} title - Sets the title
 * @property {string} subTitle - Sets the sub title
 * @property {object} firstName - Sets the firstName
 * @property {object} lastName - Sets the lastName
 * @property {object} emailAddress - Sets the emailAddress
 * @property {object} phoneNumber - Sets the phoneNumber
 * @property {object} loyalty - Sets the loyalty
 * @property {object} order - Sets the order
 * @property {object} submitAction - handle submitAction
 * @property {object} comments - Sets the comments
 * @property {number} containerLayerHostContentId - Sets the containerLayerHostContentId.
 * @property {function} invokeMutation - invokeMutation
 *
 */
export const propTypes = {
  /** Sets the title */
  title: PropTypes.string,
  /** Sets the sub title */
  subTitle: PropTypes.string,
  /** Sets the firstName */
  firstName: PropTypes.object,
  /** Sets the lastName */
  lastName: PropTypes.object,
  /** Sets the emailAddress */
  emailAddress: PropTypes.object,
  /** Sets the phoneNumber */
  phoneNumber: PropTypes.object,
  /** Sets the loyalty */
  loyalty: PropTypes.object,
  /** Sets the order */
  order: PropTypes.object,
  /** handle submitAction */
  submitAction: PropTypes.object,
  /** Sets the comments */
  comments: PropTypes.object,
  /** Sets the label allTopics */
  allTopics: PropTypes.object,
  /** Sets the containerId */
  containerLayerHostContentId: PropTypes.number,
  /** Sets the invokeMutation */
  invokeMutation: PropTypes.func
};

ContactUs.propTypes = propTypes;

export default ContactUs;